import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  BalanceIndicator,
  JournalVoucherStyles,
  ReferenceCells,
  TYPE_OPTIONS,
  fetchOpenReferences,
  lineTotals,
  money,
  setLineReference,
  validateLines,
} from "./JournalVoucherShared";
import { subCategoryOptions } from "./SubCategory";

const Edit = (props) => {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState({});
  const [chartOfAccount, setChartOfAccount] = useState({});
  const [costCenter, setCostCenter] = useState([]);
  const [budgetPlan, setBudgetPlan] = useState({});
  const [subCategory, setSubCategory] = useState({});
  const [subCategorySelected, setSubCategorySelected] = useState({});
  const [journalCode, setJournalCode] = useState([]);
  // The voucher as it was issued. The number encodes the type and the month, so
  // it only stays valid while both still match; if either is changed the number
  // has to be re-read, and put back if the change is undone.
  const [issued, setIssued] = useState({});
  const [error, setError] = useState({});
  const [debitSum, setDebitSum] = useState(0);
  const [creditSum, setCreditSum] = useState(0);
  const { id } = useParams();
  const [list, setList] = useState({});

  const [selectedJCodeOption, setSelectedJCodeOption] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Existing Reference options, keyed by account. Read with this voucher left
  // out, so what it already posts against still shows as open.
  const [openReferences, setOpenReferences] = useState({});

  const loadOpenReferences = (accountId) => {
    fetchOpenReferences(accountId, id)
      .then((options) =>
        setOpenReferences((values) => ({ ...values, [accountId]: options }))
      )
      .catch((e) => console.error("Could not load open references:", e));
  };

  // Each row's ledgers arrive keyed by row index, so the options are derived
  // once per fetch rather than on every render of every row.
  const subCategoryOptionsByRow = useMemo(() => {
    const byRow = {};
    Object.keys(subCategory).forEach((key) => {
      byRow[key] = subCategoryOptions(subCategory[key]);
    });
    return byRow;
  }, [subCategory]);

  const selectHandleChange = (selectedOption, actionMeta) => {
    if (actionMeta.name === "journal_code") {
      setSelectedJCodeOption({
        label: selectedOption.label,
        value: selectedOption.value,
      });
    }

    const name = actionMeta.name;
    const value = selectedOption.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const refreshJournalNo = (type, date) => {
    const sameMonth =
      String(date || "").slice(0, 7) === String(issued.date || "").slice(0, 7);

    if (type === issued.type && sameMonth) {
      setInputs((values) => ({ ...values, serial_number: issued.serial_number }));
      return;
    }

    Http.get("/journal-no", { params: { type: type, date: date } })
      .then((res) => {
        setInputs((values) => ({ ...values, serial_number: res.data }));
      })
      .catch(function (e) {
        console.error("Could not read the next journal number:", e);
      });
  };

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));

    // Each type has its own monthly sequence, so the number belongs to the type.
    if (name === "type") refreshJournalNo(value, inputs.date);
  };

  const handleDateChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));

    // A different month is a different sequence.
    refreshJournalNo(inputs.type, value);

    inputs.date = value;
    Http.post("/budget-plan-search", inputs)
      .then((res) => {
        setBudgetPlan(res.data);
      })
      .catch(function (e) {
        setError(e.response.data.errors);
      });
  };
  // Set Experience State
  const handleInputChange = (e, index) => {
    const { name, value } = e.target; // Chanages Experience Input Name and Value
    const inputInfo = [...list]; // Defined Experience State All Information into List Variable
    let debit = 0;
    let credit = 0;

    inputInfo[index][name] = value;
    if (name == "debit") inputInfo[index]["credit"] = "";
    else if (name == "credit") inputInfo[index]["debit"] = "";

    list.forEach((item, itemIndex) => {
      // if(itemIndex <= index){
      debit += item.debit > 0 ? parseFloat(item.debit) : 0;
      credit += item.credit > 0 ? parseFloat(item.credit) : 0;
      // }
    });

    setDebitSum(debit);
    setCreditSum(credit);
    setList(inputInfo);
  };

  // Chart of Account
  const selectHandleCOAChange = (value, index) => {
    const inputInfo = [...list]; // Defined Experience State All Information into List Variable
    inputInfo[index]["chart_of_account_id"] = value;
    // The sub category list belongs to the account, so a pick made against the
    // previous account no longer applies. So do its references.
    inputInfo[index]["sub_category"] = "";
    inputInfo[index]["reference"] = "";
    inputInfo[index]["existing_reference"] = "";

    if (value) loadOpenReferences(value);

    Http.get(
      "/subsidiary-ledger-search-by-chart-of-account/" + value + "/" + index
    )
      .then((res) => {
        setSubCategory((values) => ({ ...values, [index]: res.data }));
      })
      .catch(() => {
        setSubCategory((values) => ({ ...values, [index]: {} }));
      });

    setList(inputInfo);
  };

  const handleReferenceChange = (index, name, value) => {
    const inputInfo = [...list];
    inputInfo[index] = setLineReference(inputInfo[index], name, value);
    setList(inputInfo);
  };

  const selectHandleSubCategoryChange = (selectedOption, index) => {
    const inputInfo = [...list];
    inputInfo[index]["sub_category"] = selectedOption ? selectedOption.value : "";
    setList(inputInfo);
  };

  // Cost center is optional, so a cleared picker puts the line back to blank.
  const selectHandleCostCenterChange = (selectedOption, index) => {
    const inputInfo = [...list];
    inputInfo[index]["acc_cost_center_id"] = selectedOption
      ? selectedOption.value
      : "";
    setList(inputInfo);
  };

  useEffect(() => {
    setLoading(true);

    const fetchData = async () => {
      try {
        // The select2-shaped endpoint, so the account picker gets {value,label}
        // options the same way the create form does.
        const res = await Http.get("/chart-of-account-code-2");
        setChartOfAccount(res.data);

        const resCostCenter = await Http.get("/cost-center-select2");
        setCostCenter(resCostCenter.data);

        const res3 = await Http.get("/journalCodeSelect2");
        setJournalCode(res3.data);

        const res4 = await Http.get("/journal-voucher/" + id + "/edit");
        inputs.date = res4.data.date;
        Http.post("/budget-plan-search", inputs)
          .then((res4) => {
            setBudgetPlan(res4.data);
          })
          .catch(function (e) {
            setError(e.response.data.errors);
          });

        setInputs({
          date: res4.data.date,
          serial_number: res4.data.serial_number,
          type: res4.data.type,
          journal_code: res4.data.journal_code,
          remarks: res4.data.remarks,
        });

        // What the voucher was issued as, so the number can be restored if the
        // type or month is changed and then changed back.
        setIssued({
          type: res4.data.type,
          date: res4.data.date,
          serial_number: res4.data.serial_number,
        });

        // The journal code picker shows the code the voucher already carries.
        setSelectedJCodeOption(
          (res3.data || []).find(
            (option) => String(option.value) === String(res4.data.journal_code)
          ) || null
        );
        // setList(res4.data.items);
        setDebitSum(res4.data.total_amount);
        setCreditSum(res4.data.total_amount);

        const inputInfo = [];
        Object.keys(res4.data.items).forEach((key) => {
          let subCategoryValue = "";
          if (res4.data.items[key]["chart_of_account_id"] > 0) {
            Http.get(
              "/subsidiary-ledger-search-by-chart-of-account/" +
                res4.data.items[key]["chart_of_account_id"] +
                "/" +
                key
            ).then((response) => {
              setSubCategory((values) => ({ ...values, [key]: response.data }));

              if (res4.data.items[key]["subcategory_type"] != null) {
                if (res4.data.items[key]["subcategory_type"] == "hr") {
                  subCategoryValue =
                    "hr_" +
                    res4.data.items[key]["subcategory_ledger_id"] +
                    "_" +
                    res4.data.items[key]["hr_id"];
                  setSubCategorySelected((values) => ({
                    ...values,
                    [key]: subCategoryValue,
                  }));
                } else if (
                  res4.data.items[key]["subcategory_type"] == "vendor"
                ) {
                  subCategoryValue =
                    "vendor_" +
                    res4.data.items[key]["subcategory_ledger_id"] +
                    "_" +
                    res4.data.items[key]["supplier_id"];
                  setSubCategorySelected((values) => ({
                    ...values,
                    [key]: subCategoryValue,
                  }));
                } else if (
                  res4.data.items[key]["subcategory_type"] == "distributor"
                ) {
                  subCategoryValue =
                    "distributor_" +
                    res4.data.items[key]["subcategory_ledger_id"] +
                    "_" +
                    res4.data.items[key]["distributor_id"];
                  setSubCategorySelected((values) => ({
                    ...values,
                    [key]: subCategoryValue,
                  }));
                } else if (
                  res4.data.items[key]["subcategory_type"] == "customer"
                ) {
                  subCategoryValue =
                    "customer_" +
                    res4.data.items[key]["subcategory_ledger_id"] +
                    "_" +
                    res4.data.items[key]["customer_id"];
                  setSubCategorySelected((values) => ({
                    ...values,
                    [key]: subCategoryValue,
                  }));
                } else if (
                  res4.data.items[key]["subcategory_type"] == "asset"
                ) {
                  subCategoryValue =
                    "asset_" +
                    res4.data.items[key]["subcategory_ledger_id"] +
                    "_" +
                    res4.data.items[key]["asset_id"];
                  setSubCategorySelected((values) => ({
                    ...values,
                    [key]: subCategoryValue,
                  }));
                } else {
                  subCategoryValue =
                    "subsidiary_" +
                    res4.data.items[key]["subcategory_ledger_id"];
                  setSubCategorySelected((values) => ({
                    ...values,
                    [key]: subCategoryValue,
                  }));
                }
              }
            });
          }

          if (res4.data.items[key]["subcategory_type"] != null) {
            /*if (res4.data.items[key]["subcategory_type"] == "hr") {
            subCategoryValue =
              "hr_" +
              res4.data.items[key]["subcategory_ledger_id"] +
              "_" +
              res4.data.items[key]["hr_id"];
          } else {
            subCategoryValue =
              "subsidiary_" + res4.data.items[key]["subcategory_ledger_id"];
          }*/

            if (res4.data.items[key]["subcategory_type"] == "hr") {
              subCategoryValue =
                "hr_" +
                res4.data.items[key]["subcategory_ledger_id"] +
                "_" +
                res4.data.items[key]["hr_id"];
            } else if (res4.data.items[key]["subcategory_type"] == "vendor") {
              subCategoryValue =
                "vendor_" +
                res4.data.items[key]["subcategory_ledger_id"] +
                "_" +
                res4.data.items[key]["supplier_id"];
            } else if (
              res4.data.items[key]["subcategory_type"] == "distributor"
            ) {
              subCategoryValue =
                "distributor_" +
                res4.data.items[key]["subcategory_ledger_id"] +
                "_" +
                res4.data.items[key]["distributor_id"];
            } else if (res4.data.items[key]["subcategory_type"] == "customer") {
              subCategoryValue =
                "customer_" +
                res4.data.items[key]["subcategory_ledger_id"] +
                "_" +
                res4.data.items[key]["customer_id"];
            } else if (res4.data.items[key]["subcategory_type"] == "asset") {
              subCategoryValue =
                "asset_" +
                res4.data.items[key]["subcategory_ledger_id"] +
                "_" +
                res4.data.items[key]["asset_id"];
            } else if (
              res4.data.items[key]["subcategory_type"] == "subsidiary"
            ) {
              subCategoryValue =
                "subsidiary_" + res4.data.items[key]["subcategory_ledger_id"];
            }
          }

          inputInfo.push({
            chart_of_account_id: res4.data.items[key]["chart_of_account_id"],
            acc_cost_center_id:
              res4.data.items[key]["acc_cost_center_id"] || "",
            debit:
              parseFloat(res4.data.items[key]["debit"]) > 0
                ? res4.data.items[key]["debit"]
                : "",
            credit:
              parseFloat(res4.data.items[key]["credit"]) > 0
                ? res4.data.items[key]["credit"]
                : "",
            budget_id: res4.data.items[key]["budget_plan_item_id"] || "",
            subcategory_ledger_id:
              res4.data.items[key]["subcategory_ledger_id"],
            subcategory_type: res4.data.items[key]["subcategory_type"],
            hr_id: res4.data.items[key]["hr_id"],
            asset_id: res4.data.items[key]["asset_id"],
            supplier_id: res4.data.items[key]["supplier_id"],
            customer_id: res4.data.items[key]["customer_id"],
            distributor_id: res4.data.items[key]["distributor_id"],
            expense_id: res4.data.items[key]["expense_id"],
            sub_category: subCategoryValue,
            bill_no: res4.data.items[key]["bill_number"],
            bill_date: res4.data.items[key]["bill_date"],
            mature_day: res4.data.items[key]["mature_day"],
            remarks: res4.data.items[key]["remarks"] || "",
            reference: res4.data.items[key]["reference"] || "",
            existing_reference:
              res4.data.items[key]["existing_reference"] || "",
          });

          setList(inputInfo);
        });

        new Set(
          Object.values(res4.data.items).map((item) => item.chart_of_account_id)
        ).forEach(loadOpenReferences);
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Default Load
  // const fetchData = () => {
  //   Http.get("/chart-of-account-code").then((res) => {
  //     setChartOfAccount(res.data.chart_of_accounts);
  //   });
  //   Http.get("/journal-no").then((res) => {
  //     setJournalNo(res.data);
  //   });

  //   Http.get("/journalCodeSelect2").then((res) => {
  //     setJournalCode(res.data);
  //   });

  //   Http.get("/journal-voucher/" + id + "/edit").then((res) => {
  //     // Budget plan
  //     inputs.date = res.data.date;
  //     Http.post("/budget-plan-search", inputs)
  //       .then((res) => {
  //         setBudgetPlan(res.data);
  //       })
  //       .catch(function (e) {
  //         setError(e.response.data.errors);
  //       });

  //     setInputs({
  //       date: res.data.date,
  //       serial_number: res.data.serial_number,
  //       type: res.data.type,
  //       remarks: res.data.remarks,
  //     });
  //     // setList(res.data.items);
  //     setDebitSum(res.data.total_amount);
  //     setCreditSum(res.data.total_amount);

  //     // setSelectedJCodeOption({
  //     //   label: selectedOption.label,
  //     //   value: selectedOption.value,
  //     // });

  //     const inputInfo = [];
  //     Object.keys(res.data.items).forEach((key) => {
  //       let subCategoryValue = "";
  //       if (res.data.items[key]["chart_of_account_id"] > 0) {
  //         Http.get("/subsidiary-ledger-search-by-chart-of-account/" + res.data.items[key]["chart_of_account_id"] + "/" + key).then((response) => {
  //           setSubCategory((values) => ({ ...values, [key]: response.data }));

  //           if (res.data.items[key]["subcategory_type"] != null) {
  //             if (res.data.items[key]["subcategory_type"] == "hr") {
  //               subCategoryValue = "hr_" + res.data.items[key]["subcategory_ledger_id"] + "_" + res.data.items[key]["hr_id"];
  //               setSubCategorySelected((values) => ({
  //                 ...values,
  //                 [key]: subCategoryValue,
  //               }));
  //             } else if (res.data.items[key]["subcategory_type"] == "vendor") {
  //               subCategoryValue = "vendor_" + res.data.items[key]["subcategory_ledger_id"] + "_" + res.data.items[key]["supplier_id"];
  //               setSubCategorySelected((values) => ({
  //                 ...values,
  //                 [key]: subCategoryValue,
  //               }));
  //             } else if (res.data.items[key]["subcategory_type"] == "distributor") {
  //               subCategoryValue = "distributor_" + res.data.items[key]["subcategory_ledger_id"] + "_" + res.data.items[key]["distributor_id"];
  //               setSubCategorySelected((values) => ({
  //                 ...values,
  //                 [key]: subCategoryValue,
  //               }));
  //             } else if (res.data.items[key]["subcategory_type"] == "customer") {
  //               subCategoryValue = "customer_" + res.data.items[key]["subcategory_ledger_id"] + "_" + res.data.items[key]["customer_id"];
  //               setSubCategorySelected((values) => ({
  //                 ...values,
  //                 [key]: subCategoryValue,
  //               }));
  //             } else if (res.data.items[key]["subcategory_type"] == "asset") {
  //               subCategoryValue = "asset_" + res.data.items[key]["subcategory_ledger_id"] + "_" + res.data.items[key]["asset_id"];
  //               setSubCategorySelected((values) => ({
  //                 ...values,
  //                 [key]: subCategoryValue,
  //               }));
  //             } else {
  //               subCategoryValue = "subsidiary_" + res.data.items[key]["subcategory_ledger_id"];
  //               setSubCategorySelected((values) => ({
  //                 ...values,
  //                 [key]: subCategoryValue,
  //               }));
  //             }
  //           }
  //         });
  //       }

  //       if (res.data.items[key]["subcategory_type"] != null) {
  //         /*if (res.data.items[key]["subcategory_type"] == "hr") {
  //           subCategoryValue =
  //             "hr_" +
  //             res.data.items[key]["subcategory_ledger_id"] +
  //             "_" +
  //             res.data.items[key]["hr_id"];
  //         } else {
  //           subCategoryValue =
  //             "subsidiary_" + res.data.items[key]["subcategory_ledger_id"];
  //         }*/

  //         if (res.data.items[key]["subcategory_type"] == "hr") {
  //           subCategoryValue = "hr_" + res.data.items[key]["subcategory_ledger_id"] + "_" + res.data.items[key]["hr_id"];
  //         } else if (res.data.items[key]["subcategory_type"] == "vendor") {
  //           subCategoryValue = "vendor_" + res.data.items[key]["subcategory_ledger_id"] + "_" + res.data.items[key]["supplier_id"];
  //         } else if (res.data.items[key]["subcategory_type"] == "distributor") {
  //           subCategoryValue = "distributor_" + res.data.items[key]["subcategory_ledger_id"] + "_" + res.data.items[key]["distributor_id"];
  //         } else if (res.data.items[key]["subcategory_type"] == "customer") {
  //           subCategoryValue = "customer_" + res.data.items[key]["subcategory_ledger_id"] + "_" + res.data.items[key]["customer_id"];
  //         } else if (res.data.items[key]["subcategory_type"] == "asset") {
  //           subCategoryValue = "asset_" + res.data.items[key]["subcategory_ledger_id"] + "_" + res.data.items[key]["asset_id"];
  //         } else if (res.data.items[key]["subcategory_type"] == "subsidiary") {
  //           subCategoryValue = "subsidiary_" + res.data.items[key]["subcategory_ledger_id"];
  //         }
  //       }

  //       inputInfo.push({
  //         chart_of_account_id: res.data.items[key]["chart_of_account_id"],
  //         debit: res.data.items[key]["debit"],
  //         credit: res.data.items[key]["credit"],
  //         budget_id: res.data.items[key]["budget_plan_item_id"],
  //         subcategory_ledger_id: res.data.items[key]["subcategory_ledger_id"],
  //         subcategory_type: res.data.items[key]["subcategory_type"],
  //         hr_id: res.data.items[key]["hr_id"],
  //         asset_id: res.data.items[key]["asset_id"],
  //         supplier_id: res.data.items[key]["supplier_id"],
  //         customer_id: res.data.items[key]["customer_id"],
  //         distributor_id: res.data.items[key]["distributor_id"],
  //         expense_id: res.data.items[key]["expense_id"],
  //         sub_category: subCategoryValue,
  //         bill_no: res.data.items[key]["bill_number"],
  //         bill_date: res.data.items[key]["bill_date"],
  //         mature_day: res.data.items[key]["mature_day"],
  //       });

  //       setList(inputInfo);
  //     });
  //   });

  //   setInputs({ type: "journal" });
  // };
  // useEffect(() => {
  //   fetchData();
  // }, []);
  // End Default Load

  // Add More List
  const addMoreList = () => {
    setList([
      ...list,
      {
        chart_of_account_id: "",
        acc_cost_center_id: "",
        debit: "",
        credit: "",
        budget_id: "",
        sub_category: "",
        bill_no: "",
        bill_date: "",
        mature_day: "",
        remarks: "",
        reference: "",
        existing_reference: "",
      },
    ]); // Add One More List
  };
  // Remove List
  const removeList = (index) => {
    const inputInfo = [...list];
    inputInfo.splice(index, 1);

    const totals = lineTotals(inputInfo);
    setDebitSum(totals.debit);
    setCreditSum(totals.credit);

    // Sub category options are keyed by row index, so the rows below the one
    // removed move up a key with it.
    setSubCategory((values) => {
      const next = {};
      Object.keys(values).forEach((key) => {
        const row = Number(key);
        if (row < index) next[row] = values[key];
        else if (row > index) next[row - 1] = values[key];
      });
      return next;
    });

    setList(inputInfo);
  };

  const displayError = (fieldName) => {
    if (error && error[fieldName]) {
      return <div className="text-danger small mg-t-4">{error[fieldName]}</div>;
    }
    return null;
  };

  const formSubmit = () => {
    // An existing voucher keeps the number it was issued. This used to assign
    // the next free serial, which renumbered the voucher on every save and
    // could collide with a number already taken for that type.
    if (saving) return;

    // Validated from the list itself: reading `error` here would see the
    // previous render's value, which made a valid save after any failed one
    // do nothing until it was clicked again.
    const result = validateLines(list);
    const errors = {};
    if (!String(inputs.remarks || "").trim()) {
      errors.remarks = ["Remarks is required."];
    }
    if (result.message) {
      errors.error = [result.message];
    }
    if (Object.keys(errors).length > 0) {
      setError(errors);
      return;
    }
    setError({});

    setSaving(true);
    Http.put("/journal-voucher/" + id, {
      ...inputs,
      items: result.items,
      total_amount: result.total,
    })
      .then((res) => {
        navigate("/journal-voucher", {
          state: { message: res.data.message, type: res.data.type },
        });
      })
      .catch((e) => {
        setSaving(false);
        setError(
          e.response?.data?.errors || {
            error: [
              e.response?.data?.message ||
                "Could not update the voucher. Please try again.",
            ],
          }
        );
      });
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <JournalVoucherStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">
              Edit Journal Voucher{" "}
              {inputs.serial_number && (
                <span className="jv-serial">{inputs.serial_number}</span>
              )}
            </h5>
            <p className="text-muted mb-0 tx-12">
              Keeps its number unless the type or month is changed
            </p>
          </div>
          <div className="d-flex gap-2">
            <Link
              to={`/journal-voucher-details/${id}`}
              className="btn btn-outline-secondary"
            >
              <i className="fa fa-file-text-o mg-r-6"></i>View Voucher
            </Link>
            <Link to="/journal-voucher" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Back to List
            </Link>
          </div>
        </div>
        <div className="am-pagebody">
          <div className="row">
            <div className="col-lg">
              <FormValidation error={error} />
            </div>
          </div>
          {loading ? (
            <div className="jv-form-panel">
              <div className="jv-empty">
                <ProgressSpinner
                  style={{ width: "42px", height: "42px" }}
                  strokeWidth="6"
                />
              </div>
            </div>
          ) : (
            <>
              <div className="jv-form-panel">
                <div className="jv-form-head">
                  <span>
                    <i className="fa fa-file-text-o me-2"></i>Voucher Information
                  </span>
                </div>
                <div className="jv-form-body">
                <div className="row">
                  <div className="col-lg">
                    <label>Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="date"
                      value={inputs.date || ""}
                      onChange={handleDateChange}
                    />
                  </div>
                  <div className="col-lg">
                    <label htmlFor="">Journal Code</label>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      value={selectedJCodeOption}
                      onChange={selectHandleChange}
                      name="journal_code"
                      options={journalCode}
                    />
                    {/* <select
                  className="form-control"
                  name="journal_code"
                  onChange={handleChange}
                >
                  <option value="">Select Journal Code</option>
                </select> */}
                  </div>
                  <div className="col-lg">
                    <label htmlFor="">Journal Type</label>
                    <select
                      className="form-control"
                      name="type"
                      value={inputs.type || "journal"}
                      onChange={handleChange}
                    >
                      {TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-lg">
                    <label>Journal No.</label>
                    <input
                      type="text"
                      className="form-control"
                      name="journal_no"
                      value={inputs.serial_number || ""}
                      onChange={handleChange}
                      readOnly
                    />
                  </div>
                </div>
                </div>
              </div>

              <div className="jv-form-panel">
                <div className="jv-form-head">
                  <span>
                    <i className="fa fa-list-ul me-2"></i>Journal Lines
                  </span>
                  <BalanceIndicator debit={debitSum} credit={creditSum} />
                </div>
                <div className="jv-form-body">
                <div className="row">
                  <div className="col-lg table-responsive p-0 pb-2">
                    <table
                      className="table table-sm align-items-center mb-0 jv-line-table"
                      style={{ minWidth: "2150px" }}
                    >
                      <colgroup>
                        <col style={{ minWidth: "240px" }} />
                        <col style={{ minWidth: "220px" }} />
                        <col style={{ minWidth: "120px" }} />
                        <col style={{ minWidth: "120px" }} />
                        <col style={{ minWidth: "180px" }} />
                        <col style={{ minWidth: "200px" }} />
                        <col style={{ minWidth: "180px" }} />
                        <col style={{ minWidth: "220px" }} />
                        <col style={{ minWidth: "130px" }} />
                        <col style={{ minWidth: "150px" }} />
                        <col style={{ minWidth: "110px" }} />
                        <col style={{ minWidth: "220px" }} />
                        <col style={{ minWidth: "60px" }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>
                            Account
                          </th>
                          <th>
                            Cost Center
                          </th>
                          <th>
                            Debit
                          </th>
                          <th>
                            Credit
                          </th>
                          <th>
                            Budget
                          </th>
                          <th>
                            Sub Category
                          </th>
                          <th>
                            Reference
                          </th>
                          <th>
                            Existing Reference
                          </th>
                          <th>
                            Bill No
                          </th>
                          <th>
                            Bill Date
                          </th>
                          <th>
                            Mature Day
                          </th>
                          <th>
                            Remarks
                          </th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from(list).map((x, i) => (
                          <tr key={i}>
                            <td>
                              <div className="col-lg">
                                <Select
                                  className="basic-single"
                                  classNamePrefix="select"
                                  placeholder="Select Chart of Account"
                                  name="chart_of_account_id"
                                  options={chartOfAccount}
                                  value={
                                    Array.from(chartOfAccount).find(
                                      (item) =>
                                        String(item.value) ===
                                        String(x.chart_of_account_id)
                                    ) || null
                                  }
                                  onChange={(selectedOption) =>
                                    selectHandleCOAChange(
                                      selectedOption ? selectedOption.value : "",
                                      i
                                    )
                                  }
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                  maxMenuHeight={320}
                                  isSearchable
                                />
                              </div>
                            </td>
                            <td>
                              <div className="col-lg">
                                <Select
                                  className="basic-single"
                                  classNamePrefix="select"
                                  placeholder="Select Cost Center"
                                  name="acc_cost_center_id"
                                  options={costCenter}
                                  value={
                                    Array.from(costCenter).find(
                                      (item) =>
                                        item.value == x.acc_cost_center_id,
                                    ) || null
                                  }
                                  onChange={(selectedOption) =>
                                    selectHandleCostCenterChange(
                                      selectedOption,
                                      i,
                                    )
                                  }
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                }}
                                  maxMenuHeight={320}
                                  isClearable
                                  isSearchable
                                />
                              </div>
                            </td>
                            <td>
                              <input
                                type="text"
                                inputMode="decimal"
                                className="form-control jv-num"
                                placeholder="0.00"
                                name="debit"
                                value={x.debit || ""}
                                onChange={(e) => handleInputChange(e, i)}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                inputMode="decimal"
                                className="form-control jv-num"
                                placeholder="0.00"
                                name="credit"
                                value={x.credit || ""}
                                onChange={(e) => handleInputChange(e, i)}
                              />
                            </td>
                            <td>
                              <select
                                className="form-control"
                                name="budget_id"
                                value={x.budget_id || ""}
                                onChange={(e) => handleInputChange(e, i)}
                              >
                                <option value="">Select Budget Plan</option>
                                {Array.from(budgetPlan).map((item, index) => (
                                  <option value={item.id} key={item.id}>
                                    {item.purpose}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <div className="col-lg">
                                <Select
                                  className="basic-single"
                                  classNamePrefix="select"
                                  placeholder="Select Sub Category"
                                  name="sub_category"
                                  options={subCategoryOptionsByRow[i] || []}
                                  value={
                                    (subCategoryOptionsByRow[i] || []).find(
                                      (option) => option.value === x.sub_category
                                    ) || null
                                  }
                                  onChange={(selectedOption) =>
                                    selectHandleSubCategoryChange(
                                      selectedOption,
                                      i
                                    )
                                  }
                                  noOptionsMessage={() =>
                                    x.chart_of_account_id
                                      ? "No sub category for this account"
                                      : "Select a chart of account first"
                                  }
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                  maxMenuHeight={320}
                                  isClearable
                                  isSearchable
                                />
                              </div>
                            </td>
                            <ReferenceCells
                              line={x}
                              options={openReferences[x.chart_of_account_id]}
                              onChange={(name, value) =>
                                handleReferenceChange(i, name, value)
                              }
                            />
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                name="bill_no"
                                value={x.bill_no || ""}
                                onChange={(e) => handleInputChange(e, i)}
                              />
                            </td>
                            <td>
                              <input
                                type="date"
                                className="form-control"
                                name="bill_date"
                                value={x.bill_date || ""}
                                onChange={(e) => handleInputChange(e, i)}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                name="mature_day"
                                value={x.mature_day || ""}
                                onChange={(e) => handleInputChange(e, i)}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                name="remarks"
                                maxLength={255}
                                placeholder="Line remarks"
                                value={x.remarks || ""}
                                onChange={(e) => handleInputChange(e, i)}
                              />
                            </td>
                            <td>
                              {list.length > 2 ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm mg-t-4"
                                  onClick={() => removeList(i)}
                                  title="Remove line"
                                >
                                  <i className="fa fa-trash"></i>
                                </button>
                              ) : (
                                ""
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th>
                            Total{" "}
                            <span className="jv-head-note">
                              ({list.length} lines)
                            </span>
                          </th>
                          <th></th>
                          <th className="jv-num">{money(debitSum)}</th>
                          <th className="jv-num">{money(creditSum)}</th>
                          <th colSpan="9">
                            <BalanceIndicator
                              debit={debitSum}
                              credit={creditSum}
                            />
                          </th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={addMoreList}
                  >
                    <i className="fa fa-plus mg-r-6"></i>Add Line
                  </button>
                </div>
                </div>
              </div>

              <div className="jv-form-panel">
                <div className="jv-form-head">
                  <span>
                    <i className="fa fa-commenting-o me-2"></i>Remarks{" "}
                    <i className="fa fa-star required"></i>
                  </span>
                  <span className="jv-head-note">
                    {String(inputs.remarks || "").length}/255
                  </span>
                </div>
                <div className="jv-form-body">
                  <textarea
                    className={`form-control${
                      error && error.remarks ? " is-invalid" : ""
                    }`}
                    name="remarks"
                    rows="3"
                    maxLength={255}
                    style={{ width: "100%" }}
                    value={inputs.remarks || ""}
                    onChange={handleChange}
                    placeholder="Narration: what this voucher records and why"
                    data-required
                  />
                  {displayError("remarks")}
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 mg-b-20">
                <Link to="/journal-voucher" className="btn btn-outline-secondary">
                  Cancel
                </Link>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={formSubmit}
                  disabled={saving}
                >
                  <i
                    className={`fa ${
                      saving ? "fa-spinner fa-spin" : "fa-send"
                    } mg-r-10`}
                  ></i>
                  {saving ? "Saving..." : "Update Voucher"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Edit;
